import { type FC, Fragment, type ReactElement, startTransition, useLayoutEffect, useMemo } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { type NavigateFunction, useNavigate } from "react-router-dom"
import { useMount } from "react-use"

import { type TUserState, useUser } from "@/3514/hooks"
import {
    coachDashboardAttendancesQueryPrefetcher as attendancesPrefetcher,
    coachDashboardSessionsPrefetcher as sessionsPrefetcher
} from "@/3514/pages/coach.dashboard"
import { EUserRole } from "@/3514/types"
import { INDEX_URL } from "@/constants"
import { CoachLayout as Layout } from "@/layouts"
import PageLoader from "@/shared/spinner/PageLoader"

import { CoachWelcomeContainer as Container } from "./CoachWelcomeContainer"

import "./CoachWelcome.styles.scss"

function renderPageContent(shouldFallbackBeRendered: boolean): ReactElement {
    return !shouldFallbackBeRendered ? (
        <Layout showLogo nav={<Fragment />} withoutFooter withAccessibleDropdown>
            <Container />
        </Layout>
    ) : (
        <PageLoader />
    )
}

const Page: FC = (): ReactElement => {
    const { user }: TUserState<EUserRole.Coach> = useUser<EUserRole.Coach>()

    const navigate: NavigateFunction = useNavigate()

    const qc: QueryClient = useQueryClient()

    useLayoutEffect(
        (): void => (
            !user.areSubUsersPresent && startTransition((): void => void navigate(INDEX_URL, { replace: true })), void 0
        ),
        [user.areSubUsersPresent, navigate]
    )

    useMount((): void => {
        if (user.areSubUsersPresent) void attendancesPrefetcher({ qc })
        void sessionsPrefetcher({ qc })
    })

    return useMemo((): ReactElement => renderPageContent(!user.areSubUsersPresent), [user.areSubUsersPresent])
}

export { Page as CoachWelcomePage }
